import {
  COMPATIBILITY_DIMENSIONS,
  DEFAULT_APP_CONFIG,
  DEFAULT_MATCHING_REGION,
  interestOverlap,
  type CompatibilityDimensionId,
  type ConnectionIntent,
  type KundliScore,
  type MatchingRegion,
} from '@astrally/shared';
import { computeAshtakoota, computePorutham } from '@astrally/astro-engine';
import type { MatchableProfile } from './profile.js';
import { pairIdOf } from './profile.js';
import {
  communicationFit,
  intentionFit,
  lifeStageFit,
  reliabilityFit,
  sharedInterests,
} from './dimensions.js';

/**
 * Compatibility v3 — the canonical, deterministic 0–100 scoring engine.
 *
 * HARD RULE (from the product brief): Claude may EXPLAIN these scores but is
 * never the calculator. Everything here is pure arithmetic over profile +
 * sign-level astro summaries, versioned so cached results invalidate when
 * the formula changes.
 *
 * WHAT CHANGED IN v3 — the kundli match is the headline.
 * v2 computed the ashtakoota total and then spent it at a 0.25 share inside
 * `longTerm`, which itself carried 0.18 of the romance row: the guna count an
 * Indian user came to this app for could move their overall score by about
 * four points, less than a difference in reply-consistency. It is now its own
 * top-level dimension carrying the dominant weight for romance and a leading
 * weight everywhere else, and it is region-adjusted — Ashtakoota for a
 * northern seeker, Dasa Porutham for a southern one.
 */

export type MatchingSystem = KundliScore['system'];

export interface CompatibilityResult {
  pairId: string;
  userA: string;
  userB: string;
  overall: number;
  dimensions: Record<CompatibilityDimensionId, number>;
  structuredFactors: string[];
  highlights: string[];
  frictions: string[];
  /** Raw kundli total in its own units, or null when birth data is missing. */
  kundli: KundliScore | null;
  scoreVersion: string;
  inputsHash: string;
}

const NEUTRAL = 0.55;

type Element = 0 | 1 | 2 | 3; // fire, earth, air, water
const elementOf = (sign: number): Element => (((sign % 12) + 12) % 12 % 4) as Element;

function elementHarmony(a?: number, b?: number): number {
  if (a === undefined || b === undefined) return NEUTRAL;
  const ea = elementOf(a);
  const eb = elementOf(b);
  if (ea === eb) return 0.95;
  const complementary =
    (ea === 0 && eb === 2) || (ea === 2 && eb === 0) || (ea === 1 && eb === 3) || (ea === 3 && eb === 1);
  return complementary ? 0.8 : 0.42;
}

function similarity(a: number, b: number): number {
  return 1 - Math.abs(a - b);
}

/** Stable tiny hash (djb2) for cache invalidation. */
export function hashInputs(...parts: unknown[]): string {
  const s = JSON.stringify(parts);
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

/** Which kundli system grades a match rendered to this seeker. */
export function kundliSystemOf(seeker: Pick<MatchableProfile, 'matchingRegion'>): MatchingSystem {
  const region: MatchingRegion = seeker.matchingRegion ?? DEFAULT_MATCHING_REGION;
  return region === 'south' ? 'porutham' : 'ashtakoota';
}

/**
 * Intention-dependent overall weights. EVERY ROW SUMS TO 1 — asserted by the
 * test suite, because a row that sums to 0.97 quietly caps that intent's
 * overall at 97 and nobody notices.
 *
 * Why kundli dominates: this is an India-first astrology product, and a person
 * who opens a compatibility screen for a romance match has come for the guna
 * count. If the headline number can disagree with the kundli score by thirty
 * points, the headline is not describing the thing they asked about. 0.45 is
 * deliberately short of a majority — the score stays a blend, and a strong
 * profile fit can still lift a middling kundli — but nothing else outweighs it.
 *
 * Why it still leads outside romance: the same user asks the same question of a
 * business partner or a friend, and gana/tara/rajju compatibility is read for
 * exactly those relationships in the tradition. 0.30 keeps it first without
 * letting it swamp the signals that actually predict a working professional
 * relationship (communication, reliability, shared interests).
 *
 * The two systems are each normalised by their own maximum, and the classical
 * acceptance marks land in the same place (18/36 and 5/10 are both 0.5), which
 * is why one weight row can serve both without a per-system correction.
 */
export const COMPATIBILITY_WEIGHTS: Record<string, Record<CompatibilityDimensionId, number>> = {
  // Romance/marriage: kundli leads at 0.45, then the emotional/chemistry pair
  // that a couple actually lives inside day to day.
  romance: {
    kundli: 0.45, emotional: 0.12, communication: 0.09, chemistry: 0.11,
    stability: 0.07, longTerm: 0.1, friendship: 0.04, professional: 0.02,
  },
  // Work: kundli still leads at 0.30, but communication and the professional
  // dimension together outweigh it — a partnership is judged on how the two
  // people work, and the chart is the frame around that, not a substitute.
  professional: {
    kundli: 0.3, emotional: 0.04, communication: 0.18, chemistry: 0.01,
    stability: 0.12, longTerm: 0.07, friendship: 0.08, professional: 0.2,
  },
  // Friendship: kundli 0.30 (gana and tara are read for friendships too),
  // with the friendship dimension the clear second at 0.21.
  friendship: {
    kundli: 0.3, emotional: 0.13, communication: 0.14, chemistry: 0.03,
    stability: 0.08, longTerm: 0.07, friendship: 0.21, professional: 0.04,
  },
  // Everything else (study, creative, local, astrology-discussion, or no shared
  // intent at all): kundli 0.30 over an even spread, because we do not know
  // enough about the relationship being sought to tilt any further.
  default: {
    kundli: 0.3, emotional: 0.12, communication: 0.13, chemistry: 0.07,
    stability: 0.1, longTerm: 0.1, friendship: 0.12, professional: 0.06,
  },
};

function weightRowFor(shared: ConnectionIntent[]): Record<CompatibilityDimensionId, number> {
  if (shared.includes('romance')) return COMPATIBILITY_WEIGHTS.romance;
  if (shared.includes('professional') || shared.includes('founder') || shared.includes('mentorship')) {
    return COMPATIBILITY_WEIGHTS.professional;
  }
  if (shared.includes('friendship')) return COMPATIBILITY_WEIGHTS.friendship;
  return COMPATIBILITY_WEIGHTS.default;
}

/**
 * Run the seeker's chosen system over both Moon profiles.
 *
 * ASHTAKOOTA is evaluated in sorted-uid order because varna koota is
 * classically directional — the pair must score the same whichever side asks.
 *
 * PORUTHAM is defined bride→groom, and the matching layer does not know which
 * side is which: MatchableProfile carries no gender, by design (it carries only
 * what scoring needs, and gender is not a scoring input anywhere else). Rather
 * than assert an orientation we do not have, both directions are computed and
 * averaged, then snapped back to the system's own half-point grain. The cost of
 * that is bounded and small: of the ten poruthams only dina and stree-dirgha
 * actually change under reversal — gana, yoni, rasi, rasyadhipati, vasya, rajju
 * and vedha are symmetric, and mahendra's position series is closed under the
 * reverse count. So at most 2 of 10 points are affected, and a full-vs-nothing
 * disagreement on those two shows up as a half point each.
 */
function kundliScoreFor(
  system: MatchingSystem,
  first: { rashi: number; nakshatra: number },
  second: { rashi: number; nakshatra: number },
): KundliScore {
  if (system === 'porutham') {
    const forward = computePorutham(first, second).total;
    const reverse = computePorutham(second, first).total;
    return { system, score: Math.round(((forward + reverse) / 2) * 2) / 2, max: 10 };
  }
  return { system, score: computeAshtakoota(first, second).total, max: 36 };
}

export function computeCompatibility(
  a: MatchableProfile,
  b: MatchableProfile,
  scoreVersion: string = DEFAULT_APP_CONFIG.compatibilityVersion,
): CompatibilityResult {
  const factors: string[] = [];
  const sharedIntents = a.intents.filter((i) => b.intents.includes(i));
  // The tradition is the SEEKER's — `a` is whoever this score is being
  // rendered to. This is the one deliberately asymmetric input in the engine,
  // so it is folded into inputsHash below and callers must key their cache by
  // it (see compatibilityFor in functions/src/discovery/discoveryFns.ts).
  const system = kundliSystemOf(a);

  // --- astro inputs ---------------------------------------------------------
  const astroA = a.astro;
  const astroB = b.astro;
  const moonA = astroA?.moonSiderealSigns[0];
  const moonB = astroB?.moonSiderealSigns[0];

  let kootas: Record<string, number> | null = null;
  let kundli: KundliScore | null = null;
  if (
    astroA && astroB &&
    astroA.moonSiderealSigns.length > 0 && astroB.moonSiderealSigns.length > 0 &&
    astroA.moonNakshatras.length > 0 && astroB.moonNakshatras.length > 0
  ) {
    const [first, second] =
      a.uid.localeCompare(b.uid) <= 0 ? [astroA, astroB] : [astroB, astroA];
    const moonFirst = { rashi: first.moonSiderealSigns[0], nakshatra: first.moonNakshatras[0] };
    const moonSecond = { rashi: second.moonSiderealSigns[0], nakshatra: second.moonNakshatras[0] };

    // The eight kootas are computed whichever system carries the headline:
    // gana, nadi, yoni, bhakoot and graha-maitri are the shared Jyotish
    // substrate the emotional/chemistry/stability dimensions are built from,
    // and a southern seeker does not stop having a gana. Only the HEADLINE
    // switches system.
    const r = computeAshtakoota(moonFirst, moonSecond);
    kootas = Object.fromEntries(r.kootas.map((k) => [k.koota, k.points / k.max]));

    kundli = kundliScoreFor(system, moonFirst, moonSecond);
    factors.push(
      system === 'porutham'
        ? `Dasa Porutham ${kundli.score}/10 poruthams (South Indian reckoning)`
        : `Ashtakoota ${kundli.score}/36 guna points (North Indian reckoning)`,
    );
  } else {
    // Unchanged v2 behaviour, and it matters more now that kundli carries the
    // dominant weight: the dimension is filled at NEUTRAL rather than zero, so
    // missing birth data is never a penalty, and the fallback is stated in the
    // factor list. `kundli` stays null so no card can badge a fabricated
    // guna count.
    factors.push(
      'Moon-based Jyotish matching unavailable (birth data incomplete) — neutral weighting used, so the kundli dimension is a placeholder rather than a reading',
    );
  }

  const koota = (name: string): number => kootas?.[name] ?? NEUTRAL;
  const kundli01 = kundli ? kundli.score / kundli.max : NEUTRAL;

  const moonHarmony = elementHarmony(moonA, moonB);
  if (moonA !== undefined && moonB !== undefined) {
    factors.push(`Moon-sign element harmony ${(moonHarmony * 100).toFixed(0)}%`);
  }
  const venusMars =
    astroA && astroB
      ? (elementHarmony(astroA.venusTropicalSign, astroB.marsTropicalSign) +
          elementHarmony(astroB.venusTropicalSign, astroA.marsTropicalSign)) / 2
      : NEUTRAL;
  if (astroA && astroB) factors.push(`Venus–Mars cross harmony ${(venusMars * 100).toFixed(0)}%`);

  // --- profile inputs -------------------------------------------------------
  const comm = communicationFit(a, b);
  const interests = sharedInterests(a, b);
  const stage = lifeStageFit(a, b);
  const reliab = reliabilityFit(a, b);
  const intent = intentionFit(a, b);
  const langOverlap = a.languages.some((l) => b.languages.includes(l)) ? 1 : 0.3;
  if (interests.shared.length > 0) factors.push(`Shared interests: ${interests.shared.slice(0, 4).join(', ')}`);

  /*
   * A SMALL ADDITIVE SIGNAL over the curated interest taxonomy.
   *
   * `sharedInterests` compares lowercased free text, which is what profiles
   * held before the taxonomy existed and still tolerates. `interestOverlap`
   * is exact set math over stable `Interest.id` keys, so it rewards two people
   * who picked the SAME chip rather than two people who typed similar words —
   * a stronger claim, hence a small weight (0.08–0.10) rather than a
   * replacement. Weight is taken from the existing terms in the interest-led
   * dimensions, not added on top, so both stay bounded at 1.
   */
  const idOverlap = interestOverlap(a.interests, b.interests);
  if (idOverlap.shared.length > 0) {
    factors.push(`${idOverlap.shared.length} interest(s) matched exactly in the shared taxonomy`);
  }

  // --- dimensions (0..1) ----------------------------------------------------
  const dims01: Record<CompatibilityDimensionId, number> = {
    kundli: kundli01,
    emotional:
      0.35 * moonHarmony +
      0.25 * similarity(a.dimensions.emotionalOpenness, b.dimensions.emotionalOpenness) +
      0.2 * koota('gana') +
      0.2 * koota('nadi'),
    communication:
      0.5 * comm + 0.25 * koota('grahaMaitri') + 0.25 * langOverlap,
    chemistry:
      0.5 * venusMars + 0.3 * koota('yoni') +
      0.2 * similarity(a.dimensions.socialEnergy, b.dimensions.socialEnergy),
    stability:
      0.35 * reliab + 0.35 * koota('bhakoot') +
      0.3 * similarity(a.dimensions.replyConsistency, b.dimensions.replyConsistency),
    longTerm:
      0.35 * koota('nadi') + 0.3 * stage + 0.35 * intent,
    friendship:
      0.4 * interests.score + 0.28 * koota('gana') +
      0.22 * similarity(a.dimensions.humour, b.dimensions.humour) +
      0.1 * idOverlap.jaccard,
    professional:
      0.42 * interests.score + 0.28 * comm + 0.22 * stage + 0.08 * idOverlap.jaccard,
  };

  const clamp100 = (x: number) => Math.max(0, Math.min(100, Math.round(x * 100)));
  const dimensions = Object.fromEntries(
    COMPATIBILITY_DIMENSIONS.map((d) => [d, clamp100(dims01[d])]),
  ) as Record<CompatibilityDimensionId, number>;

  const weights = weightRowFor(sharedIntents);
  const overall = clamp100(
    COMPATIBILITY_DIMENSIONS.reduce((sum, d) => sum + dims01[d] * weights[d], 0),
  );

  // --- highlights & frictions ----------------------------------------------
  const LABEL: Record<CompatibilityDimensionId, string> = {
    kundli: system === 'porutham' ? 'Porutham match' : 'Kundli guna match',
    emotional: 'Emotional attunement',
    communication: 'Communication',
    chemistry: 'Attraction & chemistry',
    stability: 'Stability',
    longTerm: 'Long-term alignment',
    friendship: 'Friendship alignment',
    professional: 'Professional compatibility',
  };
  const ranked = [...COMPATIBILITY_DIMENSIONS].sort((x, y) => dimensions[y] - dimensions[x]);
  const highlights = ranked
    .filter((d) => dimensions[d] >= 70)
    .slice(0, 2)
    .map((d) => `${LABEL[d]} ${dimensions[d]}`);
  const frictions = ranked
    .filter((d) => dimensions[d] <= 45)
    .slice(-2)
    .map((d) => `${LABEL[d]} runs lower (${dimensions[d]}) — expect to invest here`);
  if (kootas && koota('nadi') === 0) {
    frictions.push('Same nadi (classical nadi dosha) — traditionally flagged for long-term pacing');
  }

  const [userA, userB] = [a.uid, b.uid].sort();
  // Per-user fingerprints combined in sorted-uid order so hash(a,b)===hash(b,a)
  // for the SYMMETRIC inputs. `system` sits outside that sort on purpose: it
  // comes from the seeker alone, so two users on different traditions must not
  // collide on one cache entry.
  const fingerprint = (p: MatchableProfile) => ({
    uid: p.uid,
    astro: p.astro ?? null,
    interests: [...p.interests].sort(),
    dims: p.dimensions,
    intents: [...p.intents].sort(),
    languages: [...p.languages].sort(),
    lifeStage: p.lifeStage ?? null,
    ageBand: p.ageBand,
  });
  const ordered = [fingerprint(a), fingerprint(b)].sort((x, y) => x.uid.localeCompare(y.uid));

  return {
    pairId: pairIdOf(a.uid, b.uid),
    userA,
    userB,
    overall,
    dimensions,
    structuredFactors: factors,
    highlights,
    frictions,
    kundli,
    scoreVersion,
    inputsHash: hashInputs(scoreVersion, system, ordered),
  };
}
